import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Phone, MessageSquare, FileText, History,
    AlertTriangle, Check, X, Loader2, CreditCard, Home,
    Calendar, Mail, User, Flag, Edit3, MoreVertical
} from 'lucide-react';
import { useLandlordStore, Tenant } from '@/stores/useLandlordStore';
import { useToast } from '@/contexts/ToastContext';
import { motion } from 'framer-motion';

interface TenantDetail extends Tenant {
    profile?: {
        name: string;
        profilePic?: string;
        phone?: string;
        email?: string;
    };
    tenancy?: {
        unitName: string;
        propertyName: string;
        propertyId: string;
        leaseStart?: string;
        leaseEnd?: string;
        rentAmount?: number;
    };
    isFlagged?: boolean;
    paymentHistory?: Array<{
        id: string;
        date: string;
        amount: number;
        status: 'Paid' | 'Pending' | 'Overdue';
        description: string;
    }>;
}

const TenantDetails: React.FC = () => {
    const { tenantId } = useParams<{ tenantId: string }>();
    const navigate = useNavigate();
    const { success, error: toastError } = useToast();
    const [tenant, setTenant] = useState<TenantDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [isFlagged, setIsFlagged] = useState(false);
    const [showActions, setShowActions] = useState(false);

    useEffect(() => {
        fetchTenantDetails();
    }, [tenantId]);

    const fetchTenantDetails = async () => {
        setLoading(true);
        try {
            // Try to fetch from API - if endpoint doesn't exist, construct from store data
            try {
                const apiClient = (await import('@/services/apiClient')).default;
                const response = await apiClient.get(`/tenants/profile/${tenantId}`);
                setTenant(response.data);
                setIsFlagged(response.data.isFlagged || false);
            } catch {
                // Fallback: construct from store data
                const store = useLandlordStore.getState();
                const foundTenant = store.tenants.find(t => t.id === tenantId);
                if (foundTenant) {
                    setTenant({
                        ...foundTenant,
                        profile: {
                            name: foundTenant.full_name,
                            phone: foundTenant.phone_number,
                            email: foundTenant.email
                        },
                        tenancy: {
                            unitName: foundTenant.unit_number || 'Unknown',
                            propertyName: foundTenant.property_name || 'Unknown',
                            propertyId: '',
                            leaseStart: foundTenant.lease_start,
                            leaseEnd: foundTenant.lease_end
                        },
                        isFlagged: false
                    });
                }
            }
        } catch (err: any) {
            console.error('Failed to fetch tenant details:', err);
            toastError('Failed to load tenant details');
        } finally {
            setLoading(false);
        }
    };

    const handleFlagTenant = async () => {
        try {
            const apiClient = (await import('@/services/apiClient')).default;
            await apiClient.put(`/tenants/${tenantId}/flag`, { flagged: !isFlagged });
            setIsFlagged(!isFlagged);
            success(isFlagged ? 'Tenant unflagged' : 'Tenant flagged');
        } catch (err: any) {
            console.error('Failed to update flag:', err);
            // Update local state optimistically
            setIsFlagged(!isFlagged);
        }
    };

    const handleStartChat = () => {
        // Navigate to chat with this tenant
        navigate(`/landlord/chat?tenantId=${tenantId}`);
    };

    const handleMakeCall = () => {
        if (tenant?.profile?.phone) {
            window.location.href = `tel:${tenant.profile.phone}`;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (!tenant) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen p-8">
                <AlertTriangle className="h-16 w-16 text-yellow-500 mb-4" />
                <h2 className="text-xl font-bold text-light-text dark:text-dark-text mb-2">
                    Tenant Not Found
                </h2>
                <p className="text-light-subtle dark:text-dark-subtle mb-6">
                    The tenant you're looking for doesn't exist or has been removed.
                </p>
                <button
                    onClick={() => navigate('/landlord/tenants')}
                    className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                    Back to Tenants
                </button>
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <button
                    onClick={() => navigate('/landlord/tenants')}
                    className="flex items-center gap-2 text-light-text dark:text-dark-text hover:text-primary transition-colors"
                >
                    <ArrowLeft size={20} />
                    <span>Back to Tenants</span>
                </button>
                <div className="relative">
                    <button
                        onClick={() => setShowActions(!showActions)}
                        className="p-2 hover:bg-light-bg dark:hover:bg-dark-bg rounded-lg transition-colors"
                    >
                        <MoreVertical size={20} />
                    </button>
                    {showActions && (
                        <div className="absolute right-0 top-full mt-2 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg shadow-lg z-50 min-w-[180px]">
                            <button
                                onClick={() => { handleFlagTenant(); setShowActions(false); }}
                                className="w-full flex items-center gap-2 px-4 py-3 text-sm text-light-text dark:text-dark-text hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                            >
                                <Flag size={16} />
                                {isFlagged ? 'Remove Flag' : 'Flag Tenant'}
                            </button>
                            <button
                                onClick={() => { navigate(`/landlord/tenants/${tenantId}/edit`); setShowActions(false); }}
                                className="w-full flex items-center gap-2 px-4 py-3 text-sm text-light-text dark:text-dark-text hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                            >
                                <Edit3 size={16} />
                                Edit Tenant
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Profile Header */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-light-card dark:bg-dark-card rounded-2xl p-6 shadow-sm border border-light-border dark:border-dark-border"
            >
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                    <div className="relative">
                        <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-primary to-blue-500 flex items-center justify-center text-white text-3xl font-bold">
                            {tenant.profile?.profilePic ? (
                                <img src={tenant.profile.profilePic} alt={tenant.profile.name} className="w-full h-full object-cover rounded-2xl" />
                            ) : (
                                tenant.profile?.name?.charAt(0) || 'T'
                            )}
                        </div>
                        {isFlagged && (
                            <div className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                                <Flag size={14} className="text-white" />
                            </div>
                        )}
                    </div>
                    <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                            <h1 className="text-2xl font-bold text-light-text dark:text-dark-text">
                                {tenant.profile?.name || tenant.full_name}
                            </h1>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${tenant.status === 'Active'
                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                    : tenant.status === 'Past'
                                        ? 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                                        : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                }`}>
                                {tenant.status}
                            </span>
                        </div>
                        <div className="flex items-center gap-2 text-light-subtle dark:text-dark-subtle mb-1">
                            <Home size={16} />
                            <span>Unit {tenant.tenancy?.unitName || tenant.unit_number}</span>
                            <span className="mx-2">•</span>
                            <span>{tenant.tenancy?.propertyName || tenant.property_name}</span>
                        </div>
                        {tenant.tenancy?.leaseStart && (
                            <div className="flex items-center gap-2 text-sm text-light-subtle dark:text-dark-subtle">
                                <Calendar size={14} />
                                <span>
                                    Lease: {new Date(tenant.tenancy.leaseStart).toLocaleDateString()} -
                                    {tenant.tenancy.leaseEnd ? new Date(tenant.tenancy.leaseEnd).toLocaleDateString() : 'Ongoing'}
                                </span>
                            </div>
                        )}
                    </div>
                    <div className="flex gap-3">
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={handleMakeCall}
                            className="flex items-center gap-2 px-4 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors"
                        >
                            <Phone size={18} />
                            Call
                        </motion.button>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={handleStartChat}
                            className="flex items-center gap-2 px-4 py-3 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors"
                        >
                            <MessageSquare size={18} />
                            Message
                        </motion.button>
                    </div>
                </div>
            </motion.div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 border border-light-border dark:border-dark-border">
                    <div className="text-sm text-light-subtle dark:text-dark-subtle mb-1">Monthly Rent</div>
                    <div className="text-xl font-bold text-light-text dark:text-dark-text">
                        KES {tenant.tenancy?.rentAmount?.toLocaleString() || '0'}
                    </div>
                </div>
                    <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 border border-light-border dark:border-dark-border">
                    <div className="text-sm text-light-subtle dark:text-dark-subtle mb-1">Lease Status</div>
                    <div className="text-xl font-bold text-light-text dark:text-dark-text">
                        {tenant.tenancy?.leaseEnd
                            ? new Date(tenant.tenancy.leaseEnd) > new Date() ? 'Active' : 'Expired'
                            : 'Month-to-Month'}
                    </div>
                </div>
                <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 border border-light-border dark:border-dark-border">
                    <div className="text-sm text-light-subtle dark:text-dark-subtle mb-1">Phone</div>
                    <div className="text-lg font-semibold text-light-text dark:text-dark-text">
                        {tenant.profile?.phone || tenant.phone_number || 'N/A'}
                    </div>
                </div>
                <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 border border-light-border dark:border-dark-border">
                    <div className="text-sm text-light-subtle dark:text-dark-subtle mb-1">Email</div>
                    <div className="text-lg font-semibold text-light-text dark:text-dark-text truncate">
                        {tenant.profile?.email || tenant.email || 'N/A'}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Payment History */}
                    <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                                    <CreditCard size={20} className="text-green-600 dark:text-green-400" />
                                </div>
                                <h2 className="text-lg font-bold text-light-text dark:text-dark-text">Payment History</h2>
                            </div>
                            <button
                                onClick={() => navigate(`/landlord/financials?tenantId=${tenantId}`)}
                                className="text-sm text-primary hover:underline"
                            >
                                View All
                            </button>
                        </div>
                        <div className="space-y-3">
                            {/* Mock payment history - would be fetched from API */}
                            {[1, 2, 3].map((_, i) => (
                                <div key={i} className="flex items-center justify-between p-3 bg-light-bg dark:bg-dark-bg rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${i === 0
                                                ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                                                : i === 1
                                                    ? 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400'
                                                    : 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                                            }`}>
                                            {i === 0 ? <Check size={18} /> : i === 1 ? <Clock size={18} /> : <X size={18} />}
                                        </div>
                                        <div>
                                            <div className="font-medium text-light-text dark:text-dark-text">
                                                Rent - {['January', 'December', 'November'][i]} 2024
                                            </div>
                                            <div className="text-sm text-light-subtle dark:text-dark-subtle">
                                                {['Paid on time', 'Pending', 'Overdue by 15 days'][i]}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="font-bold text-light-text dark:text-dark-text">
                                            KES {tenant.tenancy?.rentAmount?.toLocaleString() || '15,000'}
                                        </div>
                                        <div className={`text-xs font-medium ${i === 0
                                                ? 'text-green-600 dark:text-green-400'
                                                : i === 1
                                                    ? 'text-yellow-600 dark:text-yellow-400'
                                                    : 'text-red-600 dark:text-red-400'
                                            }`}>
                                            {['Paid', 'Pending', 'Overdue'][i]}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Lease History */}
                    <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                <FileText size={20} className="text-blue-600 dark:text-blue-400" />
                            </div>
                            <h2 className="text-lg font-bold text-light-text dark:text-dark-text">Lease History</h2>
                        </div>
                        <div className="space-y-4">
                            <div className="flex items-start gap-4 p-4 bg-light-bg dark:bg-dark-bg rounded-lg">
                                <div className="flex-shrink-0 w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                                    <FileText size={20} className="text-blue-600 dark:text-blue-400" />
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center justify-between mb-1">
                                        <h3 className="font-semibold text-light-text dark:text-dark-text">Current Lease</h3>
                                        <span className="px-2 py-0.5 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full text-xs font-medium">
                                            Active
                                        </span>
                                    </div>
                                    <p className="text-sm text-light-subtle dark:text-dark-subtle mb-2">
                                        Started: {tenant.tenancy?.leaseStart ? new Date(tenant.tenancy.leaseStart).toLocaleDateString() : 'N/A'}
                                        {tenant.tenancy?.leaseEnd && ` - Ends: ${new Date(tenant.tenancy.leaseEnd).toLocaleDateString()}`}
                                    </p>
                                    <div className="flex gap-4 text-sm text-light-subtle dark:text-dark-subtle">
                                        <span>Unit: {tenant.tenancy?.unitName || tenant.unit_number}</span>
                                        <span>Rent: KES {tenant.tenancy?.rentAmount?.toLocaleString() || '0'}/month</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Actions */}
                    <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                        <h3 className="text-lg font-bold text-light-text dark:text-dark-text mb-4">Quick Actions</h3>
                        <div className="space-y-2">
                            <button
                                onClick={() => navigate(`/landlord/financials/new-invoice?tenantId=${tenantId}`)}
                                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                            >
                                <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                                    <CreditCard size={18} className="text-green-600 dark:text-green-400" />
                                </div>
                                <span className="font-medium text-light-text dark:text-dark-text">Record Payment</span>
                            </button>
                            <button
                                onClick={() => navigate(`/landlord/financials/new-invoice?tenantId=${tenantId}&type=charge`)}
                                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                            >
                                <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                    <FileText size={18} className="text-blue-600 dark:text-blue-400" />
                                </div>
                                <span className="font-medium text-light-text dark:text-dark-text">Add Charge</span>
                            </button>
                            <button
                                onClick={() => navigate(`/landlord/maintenance?tenantId=${tenantId}`)}
                                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                            >
                                <div className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                                    <AlertTriangle size={18} className="text-orange-600 dark:text-orange-400" />
                                </div>
                                <span className="font-medium text-light-text dark:text-dark-text">View Maintenance</span>
                            </button>
                            <button
                                onClick={() => {/* Send notice functionality */ }}
                                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                            >
                                <div className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                                    <Mail size={18} className="text-purple-600 dark:text-purple-400" />
                                </div>
                                <span className="font-medium text-light-text dark:text-dark-text">Send Notice</span>
                            </button>
                        </div>
                    </div>

                    {/* Notes */}
                    <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                        <h3 className="text-lg font-bold text-light-text dark:text-dark-text mb-4">Notes</h3>
                        <textarea
                            placeholder="Add notes about this tenant..."
                            className="w-full p-3 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
                            rows={4}
                        />
                        <button className="mt-2 w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium">
                            Save Note
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Simple Clock component for inline use
const Clock = ({ size, className }: { size: number; className?: string }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
    </svg>
);

export default TenantDetails;